import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

type SeoConfig = {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  price?: string;
  currency?: string;
  availability?: 'instock' | 'oos';
  jsonLd?: object | object[];
  type?: 'website' | 'product';
  robots?: string;
};

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteUrl = environment.siteUrl.replace(/\/$/, '');
  private readonly jsonLdId = 'seo-json-ld';

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  update(config: SeoConfig) {
    const canonical = config.canonical ?? this.absoluteUrl('/');
    const title = this.clean(config.title, 65);
    const description = this.clean(config.description, 160);

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: config.robots ?? 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:type', content: config.type ?? 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Clave Winds' });
    this.meta.updateTag({ property: 'og:locale', content: 'es_MX' });
    this.meta.updateTag({ name: 'twitter:card', content: config.image ? 'summary_large_image' : 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });

    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
      this.meta.updateTag({ property: 'og:image:secure_url', content: config.image });
      this.meta.updateTag({ property: 'og:image:alt', content: config.imageAlt || title });
      this.meta.updateTag({ name: 'twitter:image', content: config.image });
      this.meta.updateTag({ name: 'twitter:image:alt', content: config.imageAlt || title });
    } else {
      this.meta.removeTag("property='og:image'");
      this.meta.removeTag("property='og:image:secure_url'");
      this.meta.removeTag("property='og:image:alt'");
      this.meta.removeTag("name='twitter:image'");
      this.meta.removeTag("name='twitter:image:alt'");
    }

    this.updateOptionalProperty('product:price:amount', config.price);
    this.updateOptionalProperty('product:price:currency', config.price ? (config.currency || 'MXN') : undefined);
    this.updateOptionalProperty('product:availability', config.availability);

    this.setCanonical(canonical);
    this.setJsonLd(config.jsonLd);
  }

  private clean(value: string, maxLength: number): string {
    const cleanValue = String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (cleanValue.length <= maxLength) return cleanValue;
    return `${cleanValue.slice(0, maxLength - 1).replace(/\s+\S*$/, '')}…`;
  }

  private updateOptionalProperty(property: string, content?: string) {
    if (content) {
      this.meta.updateTag({ property, content });
    } else {
      this.meta.removeTag(`property='${property}'`);
    }
  }

  absoluteUrl(path: string) {
    if (/^https?:\/\//i.test(path)) return path;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.siteUrl}${normalizedPath}`;
  }

  private setCanonical(url: string) {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setJsonLd(jsonLd?: object | object[]) {
    const current = this.document.getElementById(this.jsonLdId);
    if (current) current.remove();
    if (!jsonLd) return;

    const script = this.document.createElement('script');
    script.id = this.jsonLdId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }
}
